import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { omit } from 'lodash-es';
import { Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import { hasSufficientRoleLevel } from '@plugins/shared/helpers/auth';
import type { UserRole, UserToken } from '@plugins/shared/types/auth';

import { HIDE_TOKENS } from './auth.const';
import { AuthService } from './auth.service';

/**
 * Ensure's the user is authenticated based on a url query parameter named 'jwt'.
 * It also features a role permission check. If there's a 'role' attribute in
 * the route's data section, it will check role level privilege based on users role, f.e.
 *
 * {  path: '/admin',
 *    component: AdminComponent,
 *    canActivate: [AuthGuard],
 *    data: { role: 'editor' }       // only authenticated editors and admins can enter
 * }
 *
 * {  path: '/admin',
 *    component: AdminComponent,
 *    canActivate: [AuthGuard],
 *    data: { role: 'admin' }       // only authenticated admins can enter
 * }
 */
@Injectable()
export class AuthGuard {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly hidetokens = inject<boolean>(HIDE_TOKENS);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    if (this.authService.isLoggedIn()) {
      return of(this.checkRole(route, this.authService.userToken));
    }
    const { queryParamMap } = route;
    const [jwtParam, handoverParam] = [
      queryParamMap.get('jwt'),
      queryParamMap.get('handover'),
    ];
    if (jwtParam) {
      return this.authService
        .login(jwtParam)
        .pipe(map((user) => this.checkRole(route, user)));
    }
    if (handoverParam) {
      return this.authService.handover(handoverParam).pipe(
        map((user) => this.checkRole(route, user)),
        map((checked) => {
          if (this.hidetokens) {
            const url = this.router.parseUrl(state.url);
            url.queryParams = omit(route.queryParams, 'handover');
            return url;
          }
          return checked;
        }),
      );
    }
    const codeParam = queryParamMap.get('code');
    const exchangeUrlParam = queryParamMap.get('exchangeUrl');
    if (codeParam && exchangeUrlParam) {
      return this.authService
        .exchangeCode(codeParam, exchangeUrlParam)
        .pipe(map((user) => this.checkRole(route, user)));
    }
    // dev helper: reload emulator
    window.parent.postMessage(
      {
        cmd: 'eep.emulator.reload',
        val:
          window.location.href
            .match(/admin/)?.[0]
            ?.replace('admin', 'editor') ?? '',
      },
      '*',
    );

    return throwError(() => new Error('errors.noJwt'));
  }

  private checkRole(
    route: ActivatedRouteSnapshot,
    user: UserToken | undefined,
  ): boolean | UrlTree {
    if (route.data['role']) {
      if (
        !user ||
        !hasSufficientRoleLevel(user.role, route.data['role'] as UserRole)
      ) {
        window.parent.postMessage(
          {
            cmd: 'eep.emulator.reload',
            val: '',
          },
          '*',
        );
        return this.router.createUrlTree(['/']);
      }
    }
    return true;
  }
}
